import { lightImpactVibrate } from '../../../../../../../utils/appBridge'
import { PlansActionFilter } from '../../../api/types'

export const LUMPSUM_PLANS_ACTION_FILTERS_CLASS_NAME = 'relative z-[1] pt-[18px]'

export const DEFAULT_PLANS_ACTION_FILTERS: PlansActionFilter[] = [
  PlansActionFilter.ALL,
  PlansActionFilter.SELL,
  PlansActionFilter.BUY,
  PlansActionFilter.IN_REVIEW,
  PlansActionFilter.NO_ACTION,
  PlansActionFilter.COMPLETED,
  // PlansActionFilter.INVALIDATED
]

const LIST_PLANS_ACTION_FILTER_LABELS: Record<PlansActionFilter, string> = {
  [PlansActionFilter.ALL]: 'All actions',
  [PlansActionFilter.SELL]: 'Sell',
  [PlansActionFilter.BUY]: 'Buy',
  [PlansActionFilter.IN_REVIEW]: 'In review',
  [PlansActionFilter.NO_ACTION]: 'No action',
  [PlansActionFilter.COMPLETED]: 'Completed',
  [PlansActionFilter.INVALIDATED]: 'No longer needed'
}

type ListPlansActionFiltersProps = {
  availableFilters: PlansActionFilter[]
  activeFilter: PlansActionFilter
  onFilterChange: (filter: PlansActionFilter) => void
  inReviewLabel?: string
}

export function ListPlansActionFilters({
  availableFilters,
  activeFilter,
  onFilterChange,
  inReviewLabel = 'In review'
}: ListPlansActionFiltersProps) {
  if (availableFilters.length <= 0) {
    return null
  }

  return (
    <div className="relative z-[1] overflow-x-auto no-scrollbar overscroll-x-none px-5 pb-4 pt-1">
      <div className="flex gap-[10px] w-max">
        {availableFilters.map((filterOption) => {
          const isSelected = activeFilter === filterOption
          return (
            <div
              key={filterOption}
              className="flex w-[58px] shrink-0 flex-col items-center gap-3 cursor-pointer"
              onClick={() => {
                lightImpactVibrate()
                onFilterChange(filterOption)
              }}
            >
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full ${isSelected ? 'bg-[#232323]' : 'bg-[#FDFCFE]'
                  }`}
              >
                <ListPlansActionFilterIcon filter={filterOption} isSelected={isSelected} />
              </div>
              <span
                className={`text-center text-[12px] leading-[1.2] max-w-[64px] ${isSelected ? 'font-[500] text-[#232323]' : 'text-[#232323] opacity-50'
                  }`}
              >
                {filterOption === PlansActionFilter.IN_REVIEW
                  ? inReviewLabel
                  : LIST_PLANS_ACTION_FILTER_LABELS[filterOption]}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListPlansActionFilterIcon({
  filter,
  isSelected
}: {
  filter: PlansActionFilter
  isSelected: boolean
}) {
  const iconColor = isSelected ? '#FFFFFF' : '#6A6E6E'

  if (filter === PlansActionFilter.ALL) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M1 8L4.93844 11.5L12.2667 2.5"
          stroke={iconColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (filter === PlansActionFilter.SELL) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <rect width="14" height="14" fill="white" fillOpacity="0.0901961" />
        <path d="M1.10938 7L10.1719 7" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M5.1875 2.01562L10.1719 7L5.1875 11.9844"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.8906 11.9844L12.8906 2.01562"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (filter === PlansActionFilter.BUY) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
        <rect width="14" height="14" transform="translate(0.640625)" fill="white" fillOpacity="0.0901961" />
        <path d="M9.8125 7L0.75 7" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path
          d="M5.73438 11.9844L0.750001 7L5.73438 2.01563"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12.5312 11.9844L12.5312 2.01562"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  }

  if (filter === PlansActionFilter.IN_REVIEW) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path
          d="M5.93524 14.4252C6.70357 14.9626 7.61038 15.3008 8.58819 15.3725V17C7.18014 16.9198 5.88297 16.4082 4.816 15.5897L5.93524 14.4252ZM10.115 1.14786L8.91127 2.40037H8.97829C12.8561 2.40048 16 5.67136 16 9.7061C16 13.3259 13.4692 16.3289 10.1486 16.9088V15.2552C12.602 14.6975 14.4396 12.4261 14.4396 9.7061C14.4396 6.568 11.9944 4.02398 8.97829 4.02387H8.91127L10.115 5.27638L9.01184 6.42424L6.47619 3.78605C6.32988 3.63382 6.24762 3.42741 6.24762 3.21212C6.24764 2.99685 6.32989 2.79041 6.47619 2.63819L9.01184 0L10.115 1.14786ZM3.57257 10.5178C3.71863 11.5802 4.14715 12.5475 4.77639 13.3352L3.6701 14.4863C2.76335 13.3972 2.1604 12.0274 2 10.5178H3.57257ZM3.99772 5.79244L4.61943 6.28155C4.07545 7.0298 3.70606 7.92203 3.57257 8.89434H2.00076C2.14329 7.55512 2.63286 6.32368 3.37524 5.30253L3.99772 5.79244Z"
          fill={iconColor}
        />
      </svg>
    )
  }

  if (filter === PlansActionFilter.NO_ACTION) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
        <path
          d="M14.8198 7.9542V4.07415H10.3739V-0.00130177H7.42947L7.20658 0.27079L4.09499 4.07415H0V13.707H11.9434L14.8198 7.9542ZM5.18694 5.07997L8.13134 1.48068H8.89189V5.55613H13.3378V7.60401L11.0273 12.225H5.18694V5.07997ZM1.48198 5.55613H3.70496V12.225H1.48198V5.55613Z"
          fill={iconColor}
        />
      </svg>
    )
  }

  if (filter === PlansActionFilter.INVALIDATED) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M7 1.75L12.25 12.25H1.75L7 1.75Z"
          stroke={iconColor}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M7 5.5V8.25" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
        <path d="M7 10.5H7.005" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    )
  }

  if (filter === PlansActionFilter.COMPLETED) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M7.99902 0.75C9.92137 0.752303 11.7647 1.51667 13.124 2.87598C14.3984 4.15035 15.1494 5.85015 15.2402 7.6416L15.25 8.00098C15.2498 9.43455 14.8248 10.8363 14.0283 12.0283C13.2317 13.2205 12.0991 14.1495 10.7744 14.6982C9.44975 15.2469 7.99219 15.39 6.58594 15.1104C5.17958 14.8306 3.88698 14.1409 2.87305 13.127C1.85912 12.113 1.16939 10.8204 0.889648 9.41406C0.609982 8.00781 0.753083 6.55025 1.30176 5.22559C1.85046 3.90091 2.77953 2.76831 3.97168 1.97168C5.16366 1.17522 6.56545 0.750194 7.99902 0.75Z"
          stroke={iconColor}
          strokeWidth="1.5"
        />
        <path
          d="M11.2012 5.5C11.2668 5.50003 11.332 5.51298 11.3926 5.53809C11.4532 5.56322 11.5083 5.6001 11.5547 5.64648C11.6011 5.69289 11.638 5.74798 11.6631 5.80859C11.6882 5.86923 11.7012 5.93437 11.7012 6C11.7012 6.06563 11.6882 6.13077 11.6631 6.19141C11.638 6.252 11.601 6.30711 11.5547 6.35352L7.1543 10.7539C7.10789 10.8003 7.05279 10.8372 6.99219 10.8623C6.93155 10.8874 6.86642 10.9004 6.80078 10.9004C6.73515 10.9004 6.67 10.8874 6.60937 10.8623C6.54876 10.8372 6.49366 10.8003 6.44727 10.7539L4.44727 8.75391C4.35353 8.66017 4.30085 8.53294 4.30078 8.40039C4.30078 8.26784 4.35359 8.14065 4.44727 8.04688C4.54103 7.95311 4.66819 7.90043 4.80078 7.90039C4.93342 7.90039 5.0605 7.95308 5.1543 8.04688L6.44727 9.33887L6.80078 9.69238L7.1543 9.33887L10.8477 5.64648C10.8941 5.60013 10.9492 5.56318 11.0098 5.53809C11.0704 5.51301 11.1356 5.5 11.2012 5.5Z"
          stroke={iconColor}
          strokeWidth="1.5"
        />
      </svg>
    )
  }

  return (
    <svg width="14" height="8" viewBox="0 0 14 8" fill="none" aria-hidden>
      <path
        d="M1.5 4H12.5M10.5 1.5L12.5 4L10.5 6.5"
        stroke={iconColor}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
